import { CloseOutlined, QuestionCircleOutlined, UploadOutlined } from '@ant-design/icons'
import type { GetProp, UploadFile, UploadProps } from 'antd'
import { Button, Col, DatePicker, Form, Input, message, Row, Switch, Tooltip, Upload } from 'antd'
import dayjs from 'dayjs'
import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { AppAdmin } from 'api/app-admin'
import Breadcrumb from 'component/breadcrumb/breadcrumb'
import { logError } from 'utils/logger'

import './style.scss'

type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0]

export default function CreateAirdrop() {
  const [form] = Form.useForm()

  const [thumbnail, setThumbnail] = useState<string>('')
  const [singleFile, setSingleFile] = useState<UploadFile[]>([])
  const navigate = useNavigate()

  const handleUploadSingle = async (info: any) => {
    const { file } = info
    const formData = new FormData()
    formData.append('file', file)
    try {
      const response = await AppAdmin.uploadSingleFile(formData)
      if (response.data.success) {
        message.success('File uploaded successfully')
        setThumbnail(response.data.data.filePath)
      }
    } catch (error: any) {
      message.error(error?.message || 'File upload failed')
      logError('Upload error:', error)
    }
  }

  const beforeUploadSingle = async (file: FileType) => {
    const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png'
    if (!isJpgOrPng) {
      form.setFields([{ name: 'uploadSingle', errors: ['You can only upload JPG/PNG file!'] }])
      return false
    }
    return true
  }

  const handleOk = async () => {
    try {
      const values = await form.validateFields()
      const {
        name,
        status,
        contractAddress,
        startDate,
        endDate,
        tokenAddress,
        // tokenDecimal,
      } = values

      await AppAdmin.createAirDrop({
        name,
        contractAddress,
        desc: '',
        startDate,
        endDate,
        bannerUrl: thumbnail || '',
        isActive: status,
        tokenAddress,
        // tokenDecimal,
      })
      message.success('Airdrop created successfully!')
      navigate('/airdrop')
    } catch (error: any) {
      message.error(error?.message ? error?.message : 'Error while submitting form')
      logError('Error while submitting form:', error)
    }
  }

  const handleCancel = () => {
    navigate('/airdrop')
  }

  const additionalItems = [{ title: 'Airdrop', href: '/airdrop' }, { title: 'New' }]

  const [startDate, setStartDate] = useState<any>(null)
  const [endDate, setEndDate] = useState<any>(null)

  return (
    <div className="p2p-create">
      <Breadcrumb additionalItems={additionalItems} />
      <Form
        form={form}
        layout="vertical"
        initialValues={{ remember: true }}
        style={{ padding: '20px 10px' }}
        onFinish={handleOk}
      >
        <fieldset>
          <legend>Basic Information</legend>
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label={
                  <>
                    Name
                    <Tooltip
                      title={
                        <div className="tooltip-box">
                          <p className="tooltip-text">
                            This is your Airdrop name, please enter your chosen name as text
                          </p>
                        </div>
                      }
                      placement="right"
                      overlayInnerStyle={{
                        backgroundColor: '#ffffff',
                      }}
                    >
                      <QuestionCircleOutlined className="tooltip-icon" />
                    </Tooltip>
                  </>
                }
                name="name"
                rules={[
                  { required: true, message: 'Please input the name!' },
                  {
                    min: 3,
                    max: 50,
                    message: 'Name should be between 3 and 50 characters!',
                  },
                ]}
              >
                <Input placeholder="Enter the name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={
                  <>
                    Status
                    <Tooltip
                      title={
                        <div className="tooltip-box">
                          <p className="tooltip-text">
                            Toggle the switch to manage your Airdrop status. When the switch is{' '}
                            <strong>ON</strong>, the Airdrop is active. When the switch is{' '}
                            <strong>OFF</strong>, the Airdrop is inactive.
                          </p>
                        </div>
                      }
                      placement="right"
                      overlayInnerStyle={{
                        backgroundColor: '#ffffff',
                      }}
                    >
                      <QuestionCircleOutlined className="tooltip-icon" />
                    </Tooltip>
                  </>
                }
                name="status"
                required
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col style={{ marginBottom: 12 }} span={12}>
              <Form.Item
                label={
                  <>
                    Pool Contract Address
                    <Tooltip
                      title={
                        <div className="tooltip-box">
                          <p className="tooltip-text">
                            This is your Airdrop contract address, please enter the correct pool
                            contract address
                          </p>
                        </div>
                      }
                      placement="right"
                      overlayInnerStyle={{
                        backgroundColor: '#ffffff',
                      }}
                    >
                      <QuestionCircleOutlined className="tooltip-icon" />
                    </Tooltip>
                  </>
                }
                name="contractAddress"
                rules={[
                  {
                    required: true,
                    message: 'Please input the contract address!',
                  },
                ]}
                style={{ marginBottom: 12 }}
              >
                <Input placeholder="Enter the contract address" />
              </Form.Item>
            </Col>
            <Col style={{ marginBottom: 12 }} span={12}>
              <Form.Item
                label={
                  <>
                    Token Address
                    <Tooltip
                      title={
                        <div className="tooltip-box">
                          <p className="tooltip-text">
                            This is your Airdrop token address, please enter the correct token
                            address
                          </p>
                        </div>
                      }
                      placement="right"
                      overlayInnerStyle={{
                        backgroundColor: '#ffffff',
                      }}
                    >
                      <QuestionCircleOutlined className="tooltip-icon" />
                    </Tooltip>
                  </>
                }
                name="tokenAddress"
                rules={[
                  {
                    required: true,
                    message: 'Please input the token address!',
                  },
                ]}
                style={{ marginBottom: 12 }}
              >
                <Input placeholder="Enter the token address" />
              </Form.Item>
            </Col>
            {/* <Col style={{ marginBottom: 12 }} span={12}>
            <Form.Item
              label={
                <>
                  Token Decimal
                  <Tooltip
                    title={
                      <div className="tooltip-box">
                        <p className="tooltip-text">
                          This is your Airdrop token decimal, please
                          enter the correct token decimal
                        </p>
                      </div>
                    }
                    placement="right"
                    overlayInnerStyle={{
                      backgroundColor: "#ffffff",
                    }}
                  >
                    <QuestionCircleOutlined className="tooltip-icon" />
                  </Tooltip>
                </>
              }
              name="tokenDecimal"
              rules={[
                {
                  required: true,
                  message: "Please input the token decimal!",
                },
              ]}
              style={{ marginBottom: 12 }}
            >
              <Input type="number" min={0} placeholder="Enter the token decimal" />
            </Form.Item>
          </Col> */}
            {/* <Col span={12}><Button
            style={{ marginTop: 30 }}
            htmlType="button"
            onClick={() => {
              if (showInfo) {
                setShowInfo(!showInfo)
              } else {
                setShowInfo(!showInfo)
                getInfoContract(form.getFieldValue("contractAddress"))
              }
            }}
          >
            {!showInfo ? "View Info" : "Hide Info"}
          </Button></Col>
          <Col span={24}>
            {infoContract && showInfo && (
              <div className="info-contract">
                <Title style={{ marginTop: 0 }} level={4}>
                  Contract Information
                  <Tooltip
                    title={
                      <div className="tooltip-box">
                        <p className="tooltip-text">
                          This is your PTO address information, check the data below
                        </p>
                      </div>
                    }
                    placement="right"
                    overlayInnerStyle={{
                      backgroundColor: "#ffffff",
                    }}
                  >
                    <InfoCircleOutlined className="tooltip-icon" />
                  </Tooltip>
                </Title>
                <Descriptions
                  bordered
                  column={1}
                  style={{ marginBottom: "16px" }}
                >
                  <Descriptions.Item label="APY">
                    {infoContract.apy}
                  </Descriptions.Item>
                  <Descriptions.Item label="Boots APY">
                    {infoContract.boots_apy}
                  </Descriptions.Item>
                  <Descriptions.Item label="Funding Target">
                    {infoContract.fundingTarget}
                  </Descriptions.Item>
                  <Descriptions.Item label="Start Time">
                    {infoContract.startDate}
                  </Descriptions.Item>
                  <Descriptions.Item label="End Time">
                    {infoContract.endDate}
                  </Descriptions.Item>
                </Descriptions>
              </div>
            )}
          </Col> */}
            {/* <Col span={24}>
            <Form.Item
              label={
                <>
                  Description
                  <Tooltip
                    title={
                      <div className="tooltip-box">
                        <p className="tooltip-text">
                          This is your Airdrop description, please enter
                          your chosen description as text
                        </p>
                      </div>
                    }
                    placement="right"
                    overlayInnerStyle={{
                      backgroundColor: "#ffffff",
                    }}
                  >
                    <QuestionCircleOutlined className="tooltip-icon" />
                  </Tooltip>
                </>
              }
              name="desc"
              rules={[
                // { required: true, message: "Please input the description!" },
                {
                  min: 3,
                  max: 150,
                  message: "Description should be between 3 and 150 characters!",
                },
              ]}
            >
              <Input.TextArea rows={4} placeholder="Enter the description" />
            </Form.Item>
          </Col> */}
          </Row>
        </fieldset>
        <fieldset>
          <legend>Timeline</legend>
          <Row gutter={24}>
            <Col span={6}>
              <Form.Item
                label={
                  <>
                    Start Time (UTC)
                    <Tooltip
                      title={
                        <div className="tooltip-box">
                          <p className="tooltip-text">
                            This is your Airdrop start time pool. You can start to claim reward
                            after this time.
                          </p>
                        </div>
                      }
                      placement="right"
                      overlayInnerStyle={{
                        backgroundColor: '#ffffff',
                      }}
                    >
                      <QuestionCircleOutlined className="tooltip-icon" />
                    </Tooltip>
                  </>
                }
                name="startDate"
                rules={[{ required: true, message: 'Please select date!' }]}
              >
                <DatePicker
                  minDate={dayjs()}
                  maxDate={endDate}
                  showTime
                  value={startDate}
                  onChange={(value) => setStartDate(value)}
                  use12Hours
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                label={
                  <>
                    End Time (UTC)
                    <Tooltip
                      title={
                        <div className="tooltip-box">
                          <p className="tooltip-text">
                            This is your Airdrop end time pool. You can&apos;t claim reward after
                            this time.
                          </p>
                        </div>
                      }
                      placement="right"
                      overlayInnerStyle={{
                        backgroundColor: '#ffffff',
                      }}
                    >
                      <QuestionCircleOutlined className="tooltip-icon" />
                    </Tooltip>
                  </>
                }
                name="endDate"
                rules={[{ required: true, message: 'Please select date!' }]}
              >
                <DatePicker
                  minDate={startDate || dayjs()}
                  showTime
                  value={endDate}
                  onChange={(value) => setEndDate(value)}
                  use12Hours
                />
              </Form.Item>
            </Col>
            {/* <Col span={6}>
            <Form.Item
              label={
                <>
                  Start Claim Time
                  <Tooltip
                    title={
                      <div className="tooltip-box">
                        <p className="tooltip-text">
                          This is your Airdrop property type. Please
                          select your chosen property. After selecting a
                          property, a map will appear for you to pick the
                          location
                        </p>
                      </div>
                    }
                    placement="right"
                    overlayInnerStyle={{
                      backgroundColor: "#ffffff",
                    }}
                  >
                    <QuestionCircleOutlined className="tooltip-icon" />
                  </Tooltip>
                </>
              }
              name="startClaimDate"
              rules={[{ required: true, message: "Please select date!" }]}
            >
              <DatePicker disabled={!endDate} minDate={startClaimDate || endDate || startDate || dayjs()} maxDate={endClaimDate} showTime value={startClaimDate} onChange={(value) => setStartClaimDate(value)} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              label={
                <>
                  End Claim Time
                  <Tooltip
                    title={
                      <div className="tooltip-box">
                        <p className="tooltip-text">
                          This is your Airdrop property type. Please
                          select your chosen property. After selecting a
                          property, a map will appear for you to pick the
                          location
                        </p>
                      </div>
                    }
                    placement="right"
                    overlayInnerStyle={{
                      backgroundColor: "#ffffff",
                    }}
                  >
                    <QuestionCircleOutlined className="tooltip-icon" />
                  </Tooltip>
                </>
              }
              name="endClaimDate"
              rules={[{ required: true, message: "Please select date!" }]}
            >
              <DatePicker disabled={!startClaimDate} minDate={startClaimDate} showTime value={endClaimDate} onChange={(value) => setEndClaimDate(value)} />
            </Form.Item>
          </Col> */}
          </Row>
        </fieldset>
        <fieldset>
          <legend>Upload Picture</legend>
          <Row gutter={24}>
            <Col style={{ position: 'relative' }} span={12}>
              {thumbnail.length > 0 && (
                <>
                  <img style={{ width: '50%', border: '1px solid' }} src={thumbnail} alt="" />
                  <CloseOutlined
                    style={{
                      position: 'absolute',
                      top: 4,
                      right: '50%',
                      color: '#000',
                      fontSize: '16px',
                      cursor: 'pointer',
                      background: 'white',
                      borderRadius: '50%',
                      padding: '2px',
                      border: '1px solid',
                    }}
                    onClick={() => {
                      setThumbnail('')
                      setSingleFile([])
                    }}
                  />
                </>
              )}
            </Col>
            <Col span={12}></Col>
            <Col style={{ marginBottom: 20, marginTop: 20 }} span={12}>
              <div style={{ marginBottom: 10 }}>
                <span style={{ color: 'red' }}>*</span> Upload new banner{' '}
                <Tooltip
                  title={
                    <div className="tooltip-box">
                      <p className="tooltip-text">
                        This is your Airdrop banner. Please upload your chosen image.
                      </p>
                    </div>
                  }
                  placement="right"
                  overlayInnerStyle={{
                    backgroundColor: '#ffffff',
                  }}
                >
                  <QuestionCircleOutlined className="tooltip-icon" />
                </Tooltip>
              </div>
              <Form.Item name="uploadSingle">
                <Upload
                  accept=".jpg,.jpeg,.png"
                  fileList={singleFile}
                  customRequest={handleUploadSingle}
                  beforeUpload={beforeUploadSingle}
                >
                  <Button icon={<UploadOutlined />}>Upload Photo</Button>
                </Upload>
              </Form.Item>
              <p>You can only upload JPG/PNG file.</p>
              <p>The image should have the size of 500x200 px.</p>
            </Col>
          </Row>
        </fieldset>
        <div style={{ textAlign: 'center', paddingBottom: 5 }}>
          <Button style={{ marginRight: 15, width: 200 }} key="back" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            style={{ width: 200 }}
            disabled={thumbnail.length === 0}
            key="submit"
            type="primary"
            htmlType="submit"
          >
            Create
          </Button>
        </div>
      </Form>
    </div>
  )
}
